import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './SimulateExamV2.css';
import examTypeService from '../services/examTypeService';
import examService from '../services/examService';
import { getLinkHref } from '../services/hateoasUtils';
import { useTheme } from '../contexts/ThemeContext';
import { ensureMinimalAnswersLinkOnQuestion } from '../utils/questionUtils';
import { QuotaExceededError, isQuotaError, getQuotaMessage, normalizeQuotaPayload } from '../lib/quota.js';

// Import practice test components for answer handling
import ExamAnswerOptions from './SimulateExam/ExamAnswerOptions';
import LabTable from './practiceTest/LabTable';
import AssetDisplay from './practiceTest/AssetDisplay';
import LabValueSidebar from './practiceTest/LabValueSidebar';
import Calculator from './practiceTest/Calculator';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFlag,
  faSpinner,
  faChevronLeft,
  faChevronRight,
  faFlask,
  faCalculator,
  faCog,
  faTimes,
  faExpand,
  faTh,
  faPause,
  faPlay,
  faCoffee,
  faStop,
  faStopCircle,
  faCompress
} from '@fortawesome/free-solid-svg-icons';

// Import timer service for session-level time tracking
import examTimerService from '../services/examTimerService';
// Import exam API for HATEOAS-based exam ending
import examApi from '../services/api/examApi';

const SimulateExamV2 = () => {
  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();

  // ================================================================
  // THEME STATE - NBME vs ExamFlip Dark Mode
  // ================================================================
  const [examTheme, setExamTheme] = useState(() => {
    const saved = localStorage.getItem('simulateExamTheme');
    return saved || 'dark'; // 'dark' | 'nbme'
  });

  useEffect(() => {
    localStorage.setItem('simulateExamTheme', examTheme);
  }, [examTheme]);

  // Sync exam theme with global light/dark mode:
  // light mode (nav) → NBME appearance, dark mode (nav) → EF dark mode
  useEffect(() => {
    setExamTheme(isDarkMode ? 'dark' : 'nbme');
  }, [isDarkMode]);

  // NOTE: We intentionally do NOT force the global body[data-theme] here.
  // The exam container themes itself via `examTheme` / the `nbme-theme` class
  // and its own self-contained `--sim-*` tokens, so it stays correct regardless
  // of the app theme. Forcing body[data-theme]="dark" used to drag the global
  // sidebar nav and footer dark while the user was in light mode.

  // ================================================================
  // UI STATE for new features
  // ================================================================
  const [showLabValues, setShowLabValues] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showNavPanel, setShowNavPanel] = useState(true);
  const [showTimer, setShowTimer] = useState(true);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [fontSize, setFontSize] = useState(() => {
    return localStorage.getItem('simulateExamFontSize') || 'medium';
  });

  useEffect(() => {
    localStorage.setItem('simulateExamFontSize', fontSize);
  }, [fontSize]);

  const [autoSubmit, setAutoSubmit] = useState(() => {
    return localStorage.getItem('simulateExamAutoSubmit') === 'true';
  });

  useEffect(() => {
    localStorage.setItem('simulateExamAutoSubmit', autoSubmit.toString());
  }, [autoSubmit]);

  const fontSizeOptions = [
    { key: 'small', label: 'S', px: 13 },
    { key: 'medium', label: 'M', px: 15 },
    { key: 'large', label: 'L', px: 17 },
    { key: 'xlarge', label: 'XL', px: 20 }
  ];

  const getFontSizePx = () => (fontSizeOptions.find(o => o.key === fontSize) || fontSizeOptions[1]).px;

  // Fullscreen toggle
  const toggleFullScreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.();
      setIsFullScreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullScreen(false);
    }
  }, []);

  useEffect(() => {
    const handler = () => setIsFullScreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // ================================================================
  // ALL ORIGINAL LOGIC FROM SimulateExam.js (preserved exactly)
  // ================================================================

  // Helper function to detect and process quota errors from any API response
  const handleQuotaError = useCallback((err) => {
    const isQuotaLikeError = err.data && (
      err.data.status === 403 ||
      err.status === 403 ||
      err.data.title === 'Plan Limit Exceeded' ||
      err.data.detail ||
      err.data.usageMetrics ||
      err.data.UsageMetrics ||
      err.data.limit !== undefined ||
      err.data.usage !== undefined
    );

    if (isQuotaError(err) || isQuotaLikeError) {
      let quotaMessage;
      if (isQuotaError(err)) {
        quotaMessage = getQuotaMessage(err.quota);
      } else {
        const normalizedData = {
          ...err.data,
          usageMetrics: err.data.usageMetrics || err.data.UsageMetrics
        };
        const normalizedQuota = normalizeQuotaPayload(normalizedData);
        quotaMessage = getQuotaMessage(normalizedQuota);
      }
      return quotaMessage;
    }
    return null;
  }, []);

  const [currentView, setCurrentView] = useState('setup');
  const [examTypes, setExamTypes] = useState([]);
  const [selectedExamType, setSelectedExamType] = useState('');
  // When user explicitly selects NBME (appearance or exam type), ensure
  // the global theme preference is set to light and persisted.
  const handleSelectNbmeTheme = useCallback(() => {
    setExamTheme('nbme');
    try {
      if (typeof toggleTheme === 'function') {
        if (isDarkMode) toggleTheme();
        else localStorage.setItem('theme', 'light');
      } else {
        localStorage.setItem('theme', 'light');
      }
    } catch (e) {
      // best-effort; ignore errors
    }
  }, [isDarkMode, toggleTheme]);

  const handleExamTypeChange = useCallback((e) => {
    const val = e.target.value;
    setSelectedExamType(val);
    try {
      const parsedId = parseInt(val);
      const type = examTypes.find(t => t.id === parsedId || t.id === val || t.id.toString() === val.toString());
      const name = (type && type.name) ? (type.name || '').toLowerCase() : '';
      if (name.includes('nbme')) {
        // switch exam appearance and global theme preference
        setExamTheme('nbme');
        if (typeof toggleTheme === 'function') {
          if (isDarkMode) toggleTheme();
          else localStorage.setItem('theme', 'light');
        } else {
          localStorage.setItem('theme', 'light');
        }
      }
    } catch (err) {
      // ignore
    }
  }, [examTypes, isDarkMode, toggleTheme]);

  const handleSelectDarkTheme = useCallback(() => {
    setExamTheme('dark');
    try {
      if (typeof toggleTheme === 'function') {
        if (!isDarkMode) toggleTheme();
        else localStorage.setItem('theme', 'dark');
      } else {
        localStorage.setItem('theme', 'dark');
      }
    } catch (e) {
      // best-effort; ignore errors
    }
  }, [isDarkMode, toggleTheme]);
  const [examSession, setExamSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isQuotaErrorState, setIsQuotaErrorState] = useState(false);

  // Exam state
  const [currentBlock, setCurrentBlock] = useState(1);
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [examStartTime, setExamStartTime] = useState(null);
  const [selectedAnswer, setSelectedAnswerState] = useState(null);
  const [isAnswerSubmitted, setIsAnswerSubmitted] = useState(false);
  const [questionFlags, setQuestionFlags] = useState({});
  const [showEndBlockModal, setShowEndBlockModal] = useState(false);
  const [showEndSessionModal, setShowEndSessionModal] = useState(false);

  // Question Assets State
  const [currentQuestionAssets, setCurrentQuestionAssets] = useState([]);
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);
  const [assetsError, setAssetsError] = useState(null);

  const setSelectedAnswer = useCallback((newAnswer) => {
    setSelectedAnswerState(newAnswer);
  }, []);

  const examAnswerOptionsRef = useRef(null);
  const [submitTrigger, setSubmitTrigger] = useState(0);
  const isRestoringAnswer = useRef(false);

  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [blockTimeRemaining, setBlockTimeRemaining] = useState(0);
  const [totalExamTimeSpent, setTotalExamTimeSpent] = useState(0);
  const [breakTimeRemaining, setBreakTimeRemaining] = useState(0);
  const [sessionState, setSessionState] = useState('not_started');
  const [completedBlocks, setCompletedBlocks] = useState(new Set());
  const [activeBlock, setActiveBlock] = useState(1);
  const masterTimerRef = useRef(null);
  const [examTimerRunning, setExamTimerRunning] = useState(false);

  const handleExamTimerUpdate = useCallback(async (timeSpent) => {
    if (!examSession) return;
    try {
      const updatePayload = { timeSpent };
      await examService.updateExamSession(examSession.id, updatePayload);
    } catch (error) {
      console.error('SimulateExamV2: Error updating exam timer:', error);
    }
  }, [examSession]);

  // Exam formats configuration
  const examFormats = {
    'CBSE': {
      totalQuestions: 200, blocks: 4, questionsPerBlock: 50,
      blockTimeMinutes: 75, totalTime: 5 * 60 + 15, breakTime: 45
    },
    'STEP 1': {
      totalQuestions: 280, blocks: 7, questionsPerBlock: 40,
      blockTimeMinutes: 60, totalTime: 8 * 60, breakTime: 45
    },
    'STEP 2': {
      totalQuestions: 320, blocks: 8, questionsPerBlock: 40,
      blockTimeMinutes: 60, totalTime: 9 * 60, breakTime: 45
    },
    'USMLE Step 1': {
      totalQuestions: 280, blocks: 7, questionsPerBlock: 40,
      blockTimeMinutes: 60, totalTime: 8 * 60, breakTime: 45
    },
    'USMLE Step 2': {
      totalQuestions: 320, blocks: 8, questionsPerBlock: 40,
      blockTimeMinutes: 60, totalTime: 9 * 60, breakTime: 45
    }
  };

  const getExamFormatByType = (examType) => {
    if (!examType) return examFormats['CBSE'];
    if (examFormats[examType.name]) return examFormats[examType.name];
    switch (examType.id) {
      case 1: return examFormats['CBSE'];
      case 2: return examFormats['STEP 1'];
      case 3: return examFormats['STEP 2'];
      default:
        const nameVariations = [
          examType.name,
          examType.name?.replace('USMLE ', ''),
          examType.name?.replace('Step ', 'STEP '),
          examType.name?.replace('STEP ', 'Step ')
        ].filter(Boolean);
        for (const variation of nameVariations) {
          if (examFormats[variation]) return examFormats[variation];
        }
        return examFormats['CBSE'];
    }
  };

  useEffect(() => {
    loadExamTypes();
    return () => {
      if (masterTimerRef.current) clearInterval(masterTimerRef.current);
    };
  }, []);

  // Master timer effect
  useEffect(() => {
    if (masterTimerRef.current) {
      clearInterval(masterTimerRef.current);
      masterTimerRef.current = null;
    }

    if (sessionState === 'block_running' || sessionState === 'break_running') {
      masterTimerRef.current = setInterval(() => {
        switch (sessionState) {
          case 'block_running':
            setTimeRemaining(prev => {
              if (prev <= 1) { setSessionState('finished'); completeExam(); return 0; }
              return prev - 1;
            });
            setBlockTimeRemaining(prev => {
              if (prev <= 1) { handleBlockTimeExpired(); return 0; }
              return prev - 1;
            });
            setTotalExamTimeSpent(prev => prev + 1);
            break;
          case 'break_running':
            setTimeRemaining(prev => {
              if (prev <= 1) { setSessionState('finished'); completeExam(); return 0; }
              return prev - 1;
            });
            setBreakTimeRemaining(prev => {
              if (prev <= 1) { resumeFromBreak(); return 0; }
              return prev - 1;
            });
            setTotalExamTimeSpent(prev => prev + 1);
            break;
          default:
            break;
        }
      }, 1000);
    }

    return () => {
      if (masterTimerRef.current) {
        clearInterval(masterTimerRef.current);
        masterTimerRef.current = null;
      }
    };
  }, [sessionState]);

  // Exam timer service effect
  useEffect(() => {
    if (examSession && currentView === 'exam' && (sessionState === 'block_running' || sessionState === 'break_running') && !examTimerRunning) {
      const existingTimeSpent = examSession.timeSpent || 0;
      examTimerService.start(existingTimeSpent, handleExamTimerUpdate);
      setExamTimerRunning(true);
    }
    if (examSession && (currentView === 'completed' || sessionState === 'finished') && examTimerRunning) {
      examTimerService.stop();
      setExamTimerRunning(false);
    }
    return () => {
      if (examTimerRunning) {
        examTimerService.stop();
        setExamTimerRunning(false);
      }
    };
  }, [examSession, currentView, sessionState, examTimerRunning, handleExamTimerUpdate]);

  // Effect to handle question state when navigating
  useEffect(() => {
    if (currentView === 'exam' && questions.length > 0) {
      resetQuestionState();
      const currentQuestionData = questions[currentQuestion - 1];
      if (currentQuestionData?.id) {
        loadQuestionAssets(currentQuestionData.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestion, currentView]);

  const loadExamTypes = async () => {
    try {
      setLoading(true);
      const types = await examTypeService.getExamTypes();
      setExamTypes(types || []);
    } catch (err) {
      const quotaMessage = handleQuotaError(err);
      if (quotaMessage) {
        setError(quotaMessage);
        setIsQuotaErrorState(true);
      } else {
        setError('Failed to load exam types');
        setIsQuotaErrorState(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const startExam = async () => {
    if (loading) return;
    if (!selectedExamType) {
      setError('Please select an exam type');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setIsQuotaErrorState(false);

      const examType = examTypes.find(type =>
        type.id === selectedExamType ||
        type.id === parseInt(selectedExamType) ||
        type.id.toString() === selectedExamType
      );

      if (!examType) throw new Error('Selected exam type not found');

      const format = getExamFormatByType(examType);
      const endDate = new Date();
      endDate.setMinutes(endDate.getMinutes() + format.totalTime);

      const startExamRequest = {
        examTypeId: selectedExamType,
        type: "Simulated",
        title: `Simulated ${examType.name} Exam`,
        endDate: endDate.toISOString(),
        limit: format.totalQuestions
      };

      const session = await examService.startExamSession(startExamRequest);
      console.log('SimulateExamV2: Loading questions for block 1');
      await loadQuestionsFromSession(session, 1);

      setExamSession(session);
      setExamStartTime(new Date());
      setTimeRemaining(format.totalTime * 60);
      setBlockTimeRemaining(format.blockTimeMinutes * 60);
      setActiveBlock(1);
      setCurrentView('exam');
      setCurrentBlock(1);
      setCurrentQuestion(1);
      setSessionState('block_running');

    } catch (err) {
      const quotaMessage = handleQuotaError(err);
      if (quotaMessage) {
        setError(quotaMessage);
        setIsQuotaErrorState(true);
      } else {
        setError(err.message || 'Failed to start exam');
        setIsQuotaErrorState(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadQuestionsFromSession = async (session, blockNumber) => {
    try {
      if (!session) throw new Error('No exam session provided');
      let questions = [];

      if (session._links && session._links.questions && session._links.questions.href) {
        questions = await examService.getExamQuestionsByLink(session._links.questions.href);
      } else if (session._links && session._links.getQuestions) {
        questions = await examService.getExamQuestionsByLink(session._links.getQuestions.href);
      } else if (session.examQuestions && Array.isArray(session.examQuestions)) {
        questions = session.examQuestions;
      } else if (session.questions && Array.isArray(session.questions)) {
        questions = session.questions;
      } else if (session[session.id] && session[session.id].examQuestions) {
        questions = session[session.id].examQuestions;
      } else {
        const questionsLink = `/api/exam/${session.id}/questions`;
        questions = await examService.getExamQuestionsByLink(questionsLink);
      }

      if (questions && Array.isArray(questions) && questions.length > 0) {
        const processedQuestions = questions.map(question => {
          const processedQuestion = ensureMinimalAnswersLinkOnQuestion(question, session.id);
          if (!processedQuestion.response) {
            processedQuestion.response = {
              questionId: processedQuestion.id,
              examId: session.id,
              timeSpent: 0,
              isFlagged: false,
              isCorrect: null,
              answerIds: [],
              _links: {
                save: {
                  href: `/api/exam/${session.id}/question/${processedQuestion.id}`,
                  method: 'POST',
                  rel: 'save'
                }
              }
            };
          }
          return processedQuestion;
        });

        const questionsPerBlock = getQuestionsPerBlock();
        const startIndex = (blockNumber - 1) * questionsPerBlock;
        const endIndex = startIndex + questionsPerBlock;
        const blockQuestions = processedQuestions.slice(startIndex, endIndex);

        setQuestions(blockQuestions);

        if (!window.examAllQuestions) {
          window.examAllQuestions = processedQuestions;
        }
      } else {
        throw new Error('No questions received or invalid format');
      }
    } catch (err) {
      const quotaMessage = handleQuotaError(err);
      if (quotaMessage) {
        setError(quotaMessage);
      } else {
        setError('Failed to load questions');
      }
      throw err;
    }
  };

  const loadQuestions = async (blockNumber) => {
    try {
      setLoading(true);
      const startIndex = (blockNumber - 1) * getQuestionsPerBlock();
      const endIndex = startIndex + getQuestionsPerBlock();

      if (window.examAllQuestions && window.examAllQuestions.length > 0) {
        const blockQuestions = window.examAllQuestions.slice(startIndex, endIndex);
        setQuestions(blockQuestions);
        resetQuestionState();
      } else {
        setError('No questions available for this block');
      }
    } catch (err) {
      const quotaMessage = handleQuotaError(err);
      if (quotaMessage) {
        setError(quotaMessage);
      } else {
        setError('Failed to load questions for this block');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetQuestionState = useCallback(() => {
    // Mark that we're restoring a previous answer so auto-submit doesn't fire
    isRestoringAnswer.current = true;
    setTimeout(() => { isRestoringAnswer.current = false; }, 300);

    const currentQuestionData = questions[currentQuestion - 1];

    if (currentQuestionData) {
      if (currentQuestionData.response?.answerIds && currentQuestionData.response.answerIds.length > 0) {
        setIsAnswerSubmitted(true);
        const existingAnswer = answers[currentQuestionData.id];
        if (existingAnswer?.selectedLetter) {
          setSelectedAnswer(existingAnswer.selectedLetter);
          return;
        }
        const sessionKey = `exam_answers_${examSession?.id || 'current'}`;
        const sessionAnswers = JSON.parse(sessionStorage.getItem(sessionKey) || '{}');
        if (sessionAnswers[currentQuestionData.id]?.selectedLetter) {
          setSelectedAnswer(sessionAnswers[currentQuestionData.id].selectedLetter);
          return;
        }
        setSelectedAnswer(null);
        return;
      }

      const previousAnswer = answers[currentQuestionData.id];
      if (previousAnswer?.selectedLetter) {
        setSelectedAnswer(previousAnswer.selectedLetter);
        setIsAnswerSubmitted(true);
        return;
      }

      const sessionKey = `exam_answers_${examSession?.id || 'current'}`;
      const sessionAnswers = JSON.parse(sessionStorage.getItem(sessionKey) || '{}');
      if (sessionAnswers[currentQuestionData.id]?.selectedLetter) {
        const sessionAnswer = sessionAnswers[currentQuestionData.id];
        setSelectedAnswer(sessionAnswer.selectedLetter);
        setIsAnswerSubmitted(sessionAnswer.isSubmitted || false);
        return;
      }
    }

    setSelectedAnswer(null);
    setIsAnswerSubmitted(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestion, examSession?.id]);

  const handleAnswersLoaded = useCallback((answersData) => {}, []);

  const handleAnswerSubmitted = (responseData) => {
    if (responseData) {
      setIsAnswerSubmitted(true);
      const currentQuestionData = questions[currentQuestion - 1];
      if (currentQuestionData && responseData.answerIds && responseData.answerIds.length > 0) {
        const answerData = {
          answer: responseData.answerIds[0],
          selectedLetter: selectedAnswer,
          isCorrect: responseData.isCorrect || false,
          submittedAt: new Date().toISOString(),
          timeSpent: responseData.timeSpent || 0,
          isFlagged: responseData.isFlagged || false
        };

        setAnswers(prev => ({
          ...prev,
          [currentQuestionData.id]: answerData
        }));

        const sessionKey = `exam_answers_${examSession?.id || 'current'}`;
        const sessionAnswers = JSON.parse(sessionStorage.getItem(sessionKey) || '{}');
        sessionAnswers[currentQuestionData.id] = { ...answerData, isSubmitted: true };
        sessionStorage.setItem(sessionKey, JSON.stringify(sessionAnswers));

        if (currentQuestionData.response) {
          currentQuestionData.response.answerIds = responseData.answerIds;
          currentQuestionData.response.isCorrect = responseData.isCorrect;
          currentQuestionData.response.timeSpent = responseData.timeSpent;
          currentQuestionData.response.isFlagged = responseData.isFlagged;
          currentQuestionData.response.timestamp = new Date().toISOString();
        }
      }
    }
  };

  const toggleQuestionFlag = (questionId) => {
    setQuestionFlags(prev => {
      const newFlags = { ...prev, [questionId]: !prev[questionId] };
      const sessionKey = `exam_flags_${examSession?.id || 'current'}`;
      sessionStorage.setItem(sessionKey, JSON.stringify(newFlags));
      return newFlags;
    });
  };

  const isQuestionFlagged = (questionId) => questionFlags[questionId] || false;

  const loadQuestionFlags = useCallback(() => {
    if (!examSession?.id) return;
    const sessionKey = `exam_flags_${examSession.id}`;
    const sessionFlags = JSON.parse(sessionStorage.getItem(sessionKey) || '{}');
    if (Object.keys(sessionFlags).length > 0) {
      setQuestionFlags(sessionFlags);
    }
  }, [examSession?.id]);

  const loadQuestionAssets = useCallback(async (questionId) => {
    if (!questionId) return;
    setIsLoadingAssets(true);
    setAssetsError(null);
    try {
      const assets = await examApi.followHateoasLink(`/api/question/${questionId}/assets`);
      setCurrentQuestionAssets(Array.isArray(assets) ? assets : []);
    } catch (error) {
      if (error.status === 404) {
        setCurrentQuestionAssets([]);
        setAssetsError(null);
      } else {
        console.error('Error loading question assets:', error);
        setAssetsError(error.message || 'Failed to load assets');
        setCurrentQuestionAssets([]);
      }
    } finally {
      setIsLoadingAssets(false);
    }
  }, []);

  const getExamFormat = () => {
    if (!selectedExamType || !examTypes.length) return examFormats['CBSE'];
    const examType = examTypes.find(type =>
      type.id === selectedExamType ||
      type.id === parseInt(selectedExamType) ||
      type.id.toString() === selectedExamType
    );
    if (!examType) return examFormats['CBSE'];
    return getExamFormatByType(examType);
  };

  const getQuestionsPerBlock = () => getExamFormat().questionsPerBlock;
  const getTotalBlocks = () => getExamFormat().blocks;

  const handleTakeBreak = () => {
    const breakDuration = 15 * 60;
    setBreakTimeRemaining(breakDuration);
    setCurrentView('break');
    setSessionState('break_running');
  };

  const handlePauseResume = () => {
    if (sessionState === 'block_running') setSessionState('paused');
    else if (sessionState === 'paused') setSessionState('block_running');
  };

  const handleBlockTimeExpired = async () => {
    setSessionState('paused');
    setCompletedBlocks(prev => new Set([...prev, currentBlock]));
    window.confirm(
      `Time's up for Block ${currentBlock}!\n\nThis block will now be closed and you cannot return to it.\n\nClick OK to continue to the break period before the next block.`
    );
    if (currentBlock < getTotalBlocks()) {
      setActiveBlock(currentBlock + 1);
      handleTakeBreak();
    } else {
      completeExam();
    }
  };

  const endCurrentBlock = () => setShowEndBlockModal(true);

  const confirmEndBlock = async () => {
    setShowEndBlockModal(false);
    const format = getExamFormat();
    const fullBlockDuration = format.blockTimeMinutes * 60;
    setTimeRemaining(prev => Math.max(0, prev - fullBlockDuration));
    setSessionState('paused');
    setCompletedBlocks(prev => new Set([...prev, currentBlock]));
    if (currentBlock < getTotalBlocks()) {
      setActiveBlock(currentBlock + 1);
      handleTakeBreak();
    } else {
      completeExam();
    }
  };

  const cancelEndBlock = () => setShowEndBlockModal(false);
  const handleConfirmEndSession = () => { setShowEndSessionModal(false); completeExam(); };
  const handleCancelEndSession = () => setShowEndSessionModal(false);

  const resumeFromBreak = async () => {
    setSessionState('paused');
    setBreakTimeRemaining(0);
    setCurrentView('exam');
    if (activeBlock > currentBlock) {
      await navigateToBlock(activeBlock);
    } else {
      setSessionState('block_running');
    }
  };

  const skipBreak = async () => await resumeFromBreak();

  const navigateToQuestion = (questionNumber) => {
    if (questionNumber >= 1 && questionNumber <= getQuestionsPerBlock()) {
      setCurrentQuestion(questionNumber);
      resetQuestionState();
    }
  };

  const navigateToBlock = async (blockNumber) => {
    if (blockNumber > activeBlock) {
      alert(`Block ${blockNumber} is not yet available. You must complete the current block first.`);
      return;
    }
    if (completedBlocks.has(blockNumber) && currentView === 'exam') {
      alert(`Block ${blockNumber} has ended and you cannot go back to it.\n\nOnce a block is completed, it cannot be accessed again.\n\nPlease continue with the current block or take a break.`);
      return;
    }
    if (blockNumber >= 1 && blockNumber <= getTotalBlocks()) {
      setCurrentBlock(blockNumber);
      setCurrentQuestion(1);
      await loadQuestions(blockNumber);
      if (blockNumber === activeBlock && !completedBlocks.has(blockNumber)) {
        const format = getExamFormat();
        setBlockTimeRemaining(format.blockTimeMinutes * 60);
        setSessionState('block_running');
      }
      resetQuestionState();
    }
  };

  const completeExam = async () => {
    try {
      setSessionState('finished');
      let finalTimeSpent = 0;
      if (examTimerRunning) {
        examTimerService.forceUpdate();
        finalTimeSpent = examTimerService.stop();
        setExamTimerRunning(false);
      } else {
        finalTimeSpent = examTimerService.getCurrentTimeSpent();
      }

      if (examSession && examSession._links) {
        const endLink = getLinkHref(examSession._links, 'end');
        if (endLink) {
          await examApi.endExam(endLink, finalTimeSpent);
        } else {
          await examService.endExamSession(examSession.id, { TimeSpent: finalTimeSpent });
        }
      } else {
        await examService.endExamSession(examSession.id, { TimeSpent: finalTimeSpent });
      }
      setCurrentView('completed');
    } catch (err) {
      setError('Failed to complete exam');
      console.error('Error completing exam:', err);
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatTimeVerbose = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours} hr ${minutes} min ${secs.toString().padStart(2, '0')} sec`;
    }
    return `${minutes} min ${secs.toString().padStart(2, '0')} sec`;
  };

  const resetExam = () => {
    if (masterTimerRef.current) clearInterval(masterTimerRef.current);
    if (examTimerRunning) {
      examTimerService.stop();
      setExamTimerRunning(false);
    }
    setCurrentView('setup');
    setSelectedExamType('');
    setExamSession(null);
    setCurrentBlock(1);
    setCurrentQuestion(1);
    setQuestions([]);
    setAnswers({});
    setTimeRemaining(0);
    setBlockTimeRemaining(0);
    setTotalExamTimeSpent(0);
    setBreakTimeRemaining(0);
    setSessionState('not_started');
    setCompletedBlocks(new Set());
    setActiveBlock(1);
    setExamStartTime(null);
    setError('');
    setIsQuotaErrorState(false);
    setSelectedAnswer(null);
    setIsAnswerSubmitted(false);
    setQuestionFlags({});
    setSubmitTrigger(0);
    setShowLabValues(false);
    setShowCalculator(false);
    setShowSettings(false);
    setShowNavPanel(true);
    window.examAllQuestions = null;
  };

  const loadExamProgress = useCallback(() => {
    if (!examSession?.id) return;
    const sessionKey = `exam_answers_${examSession.id}`;
    const sessionAnswers = JSON.parse(sessionStorage.getItem(sessionKey) || '{}');
    if (Object.keys(sessionAnswers).length > 0) {
      const localAnswers = {};
      Object.keys(sessionAnswers).forEach(questionId => {
        const sa = sessionAnswers[questionId];
        localAnswers[questionId] = {
          answer: sa.answer,
          selectedLetter: sa.selectedLetter,
          isCorrect: sa.isCorrect,
          submittedAt: sa.submittedAt,
          timeSpent: sa.timeSpent,
          isFlagged: sa.isFlagged
        };
      });
      setAnswers(localAnswers);
      return true;
    }
    return false;
  }, [examSession?.id]);

  useEffect(() => {
    if (examSession?.id && currentView === 'exam') {
      loadExamProgress();
      loadQuestionFlags();
    }
  }, [examSession?.id, currentView, loadExamProgress, loadQuestionFlags]);

  const clearExamProgress = useCallback(() => {
    if (!examSession?.id) return;
    sessionStorage.removeItem(`exam_answers_${examSession.id}`);
    sessionStorage.removeItem(`exam_flags_${examSession.id}`);
  }, [examSession?.id]);

  // Expose clearExamProgress for external use (testing, completion, etc.)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.clearExamProgress = clearExamProgress;
    }
  }, [clearExamProgress]);

  // Debug helper - expose state for testing
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.examDebugState = {
        currentQuestion,
        currentBlock,
        selectedAnswer,
        isAnswerSubmitted,
        answers,
        questions: questions.map(q => ({
          id: q.id,
          hasResponse: !!q.response,
          responseAnswerIds: q.response?.answerIds
        })),
        sessionStorage: {
          answers: JSON.parse(sessionStorage.getItem(`exam_answers_${examSession?.id || 'current'}`) || '{}'),
          flags: JSON.parse(sessionStorage.getItem(`exam_flags_${examSession?.id || 'current'}`) || '{}')
        }
      };
    }
  }, [currentQuestion, currentBlock, selectedAnswer, isAnswerSubmitted, answers, questions, examSession?.id]);

  // Auto-submit: when autoSubmit is on and user picks an answer, submit immediately
  useEffect(() => {
    if (!autoSubmit || !selectedAnswer || isRestoringAnswer.current) return;

    const timer = setTimeout(() => {
      if (examAnswerOptionsRef.current?.submitAnswer) {
        examAnswerOptionsRef.current.submitAnswer();
      } else {
        setSubmitTrigger(prev => prev + 1);
      }
    }, 150);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAnswer, autoSubmit]);

  // ================================================================
  // RENDER HELPERS
  // ================================================================
  const themeClass = examTheme === 'nbme' ? 'sim-v2 nbme-theme' : 'sim-v2';
  const currentQuestionData = currentView === 'exam' ? questions[currentQuestion - 1] : null;

  // ================================================================
  // SETTINGS PANEL RENDER
  // ================================================================
  const renderSettings = () => (
    <div className="sim-v2__settings-overlay" onClick={() => setShowSettings(false)}>
      <div className="sim-v2__settings-panel" onClick={(e) => e.stopPropagation()}>
        <div className="sim-v2__settings-header">
          <h2>Settings</h2>
          <button className="sim-v2__settings-close" onClick={() => setShowSettings(false)}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className="sim-v2__settings-body">
          {/* Appearance */}
          <div className="sim-v2__settings-section">
            <div className="sim-v2__settings-section-title">Appearance</div>
            <div className="sim-v2__theme-switcher">
              <div
                className={`sim-v2__theme-option ${examTheme === 'nbme' ? 'active' : ''}`}
                onClick={handleSelectNbmeTheme}
              >
                <div className="sim-v2__theme-icon nbme-icon">N</div>
                <div className="sim-v2__theme-name">NBME</div>
                <div className="sim-v2__theme-radio">
                  <div className="sim-v2__theme-radio-inner" />
                </div>
              </div>
              <div
                className={`sim-v2__theme-option ${examTheme === 'dark' ? 'active' : ''}`}
                onClick={handleSelectDarkTheme}
              >
                <div className="sim-v2__theme-icon dark-icon">EF</div>
                <div className="sim-v2__theme-name">Dark Mode</div>
                <div className="sim-v2__theme-radio">
                  <div className="sim-v2__theme-radio-inner" />
                </div>
              </div>
            </div>
          </div>

          {/* Font Size */}
          <div className="sim-v2__settings-section">
            <div className="sim-v2__settings-section-title">Font Size</div>
            <div className="sim-v2__font-size-control">
              <div className="sim-v2__font-size-options">
                {fontSizeOptions.map(opt => (
                  <button
                    key={opt.key}
                    className={`sim-v2__font-size-btn ${fontSize === opt.key ? 'active' : ''}`}
                    onClick={() => setFontSize(opt.key)}
                    title={`${opt.key.charAt(0).toUpperCase() + opt.key.slice(1)} (${opt.px}px)`}
                    style={{ fontSize: `${opt.px}px` }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="sim-v2__font-size-preview" style={{ fontSize: `${getFontSizePx()}px` }}>
                The quick brown fox jumps over the lazy dog.
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="sim-v2__settings-section">
            <div className="sim-v2__settings-section-title">Features</div>
            <div className="sim-v2__settings-item">
              <span className="sim-v2__settings-label">Show Timer</span>
              <label className="sim-v2__toggle">
                <input
                  type="checkbox"
                  checked={showTimer}
                  onChange={(e) => setShowTimer(e.target.checked)}
                />
                <span className="sim-v2__toggle-slider" />
              </label>
            </div>
            <div className="sim-v2__settings-item">
              <div style={{ flex: 1 }}>
                <span className="sim-v2__settings-label">Auto Submit</span>
                <p className="sim-v2__settings-hint">
                  When enabled, selecting an answer option will automatically
                  submit your answer. You can still change it by selecting another
                  option, it will update your submission immediately.
                </p>
              </div>
              <label className="sim-v2__toggle">
                <input
                  type="checkbox"
                  checked={autoSubmit}
                  onChange={(e) => setAutoSubmit(e.target.checked)}
                />
                <span className="sim-v2__toggle-slider" />
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // ================================================================
  // SETUP VIEW
  // ================================================================
  if (currentView === 'setup') {
    return (
      <div className={themeClass}>
        <div className="sim-v2__setup">
          <div className="sim-v2__setup-card">
            <h1 className="sim-v2__setup-title">Simulate Exam</h1>
            <p className="sim-v2__setup-subtitle">Select your exam type and begin a timed, block-based simulated exam experience</p>

            {error && (
              <div className="sim-v2__error">
                {error}
                {isQuotaErrorState && (
                  <div style={{ marginTop: '8px' }}>
                    <Link to="/usage">Click Here to Upgrade your plan</Link>
                  </div>
                )}
              </div>
            )}

            <div className="sim-v2__setup-form">
              <div className="form-group">
                <label className="form-label">Exam Type</label>
                <select
                  className="form-select"
                  value={selectedExamType}
                  onChange={handleExamTypeChange}
                  disabled={loading}
                >
                  <option value="">-- Select Exam Type --</option>
                  {examTypes.map(type => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>

              {selectedExamType && (
                <div className="sim-v2__format-details">
                  <h4>Exam Format</h4>
                  <div className="sim-v2__format-grid">
                    <div className="sim-v2__format-item">
                      <span className="label">Total Questions</span>
                      <span className="value">{getExamFormat().totalQuestions}</span>
                    </div>
                    <div className="sim-v2__format-item">
                      <span className="label">Blocks</span>
                      <span className="value">{getExamFormat().blocks}</span>
                    </div>
                    <div className="sim-v2__format-item">
                      <span className="label">Questions/Block</span>
                      <span className="value">{getExamFormat().questionsPerBlock}</span>
                    </div>
                    <div className="sim-v2__format-item">
                      <span className="label">Time/Block</span>
                      <span className="value">{getExamFormat().blockTimeMinutes} min</span>
                    </div>
                    <div className="sim-v2__format-item">
                      <span className="label">Total Time</span>
                      <span className="value">{Math.floor(getExamFormat().totalTime / 60)}h {getExamFormat().totalTime % 60}m</span>
                    </div>
                    <div className="sim-v2__format-item">
                      <span className="label">Break Time</span>
                      <span className="value">{getExamFormat().breakTime} min</span>
                    </div>
                  </div>
                </div>
              )}

              <button
                className="sim-v2__start-btn"
                onClick={startExam}
                disabled={loading || !selectedExamType}
              >
                {loading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    Starting Exam...
                  </>
                ) : (
                  'Start Exam'
                )}
              </button>
            </div>

          </div>
        </div>
      </div>
    );
  }

  // ================================================================
  // BREAK VIEW
  // ================================================================
  if (currentView === 'break') {
    return (
      <div className={themeClass}>
        <div className="sim-v2__break">
          <div className="sim-v2__break-card">
            <h2 className="sim-v2__break-title">Break Time</h2>
            <p className="sim-v2__break-subtitle">Block {completedBlocks.size} completed</p>

            <div className="sim-v2__break-timer-box">
              <div className="sim-v2__break-timer-main">{formatTime(breakTimeRemaining)}</div>
              <div className="sim-v2__break-timer-sub">remaining break time</div>
              <div className="sim-v2__break-total">
                <div className={`sim-v2__break-total-value ${timeRemaining < 1800 ? 'warning' : ''}`}>
                  {formatTime(timeRemaining)}
                </div>
                <div className="sim-v2__break-total-label">total exam time remaining</div>
              </div>
            </div>

            <div className="sim-v2__break-info">
              <p>You are currently on a break between blocks.</p>
              <p><strong>⚠️ Break time counts against your total exam time.</strong></p>
              <p>You can resume the exam at any time.</p>
            </div>

            {activeBlock <= getTotalBlocks() && (
              <div className="sim-v2__break-next">
                <h4>Next: Block {activeBlock}</h4>
                <p>{getQuestionsPerBlock()} questions &bull; {getExamFormat().blockTimeMinutes} minutes</p>
              </div>
            )}

            <div className="sim-v2__break-actions">
              <button className="sim-v2__break-resume" onClick={resumeFromBreak}>
                {activeBlock <= getTotalBlocks() ? `Start Block ${activeBlock}` : 'Continue Exam'}
              </button>
              {breakTimeRemaining > 0 && (
                <button className="sim-v2__break-skip" onClick={skipBreak}>Skip Break</button>
              )}
            </div>

            {activeBlock > getTotalBlocks() && (
              <div style={{ marginTop: '20px', padding: '16px', background: 'var(--sim-success-light)', borderRadius: '8px', color: 'var(--sim-success)', fontWeight: 600 }}>
                🎉 All blocks completed! You can end the exam when ready.
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ================================================================
  // COMPLETION VIEW
  // ================================================================
  if (currentView === 'completed') {
    return (
      <div className={themeClass}>
        <div className="sim-v2__complete">
          <div className="sim-v2__complete-card">
            <h2 className="sim-v2__complete-title">🎉 Exam Completed!</h2>
            <p className="sim-v2__complete-text">
              Congratulations! You have successfully completed the simulated exam.
              Your answers have been submitted and will be processed.
            </p>
            <div className="sim-v2__summary">
              <h3>Exam Summary</h3>
              <div className="sim-v2__summary-row">
                <span className="sim-v2__summary-label">Questions Answered</span>
                <span className="sim-v2__summary-value">{Object.keys(answers).length}</span>
              </div>
              <div className="sim-v2__summary-row">
                <span className="sim-v2__summary-label">Total Questions</span>
                <span className="sim-v2__summary-value">{getExamFormat().totalQuestions}</span>
              </div>
              <div className="sim-v2__summary-row">
                <span className="sim-v2__summary-label">Time Started</span>
                <span className="sim-v2__summary-value">{examStartTime?.toLocaleTimeString()}</span>
              </div>
              <div className="sim-v2__summary-row">
                <span className="sim-v2__summary-label">Time Completed</span>
                <span className="sim-v2__summary-value">{new Date().toLocaleTimeString()}</span>
              </div>
            </div>
            <div className="sim-v2__complete-actions">
              <button className="sim-v2__btn-secondary" onClick={resetExam}>Take Another Exam</button>
              <button className="sim-v2__btn-primary" onClick={() => navigate('/dashboard')}>Dashboard</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ================================================================
  // EXAM VIEW - Main redesigned interface
  // ================================================================
  if (currentView === 'exam') {
    return (
      <div className={themeClass}>
        {/* ===== TOP TOOLBAR ===== */}
        <div className="sim-v2__toolbar">
          {/* Left: Exam info + Mark */}
          <div className="sim-v2__toolbar-left">
            <div className="exam-section-info">
              <span className="item-info">
                Exam Section: Block {currentBlock} of {getTotalBlocks()}
              </span>
              <span style={{ opacity: 0.5 }}>|</span>
              <span>Item {currentQuestion} of {getQuestionsPerBlock()}</span>
            </div>
            {currentQuestionData && (
              <button
                className={`mark-btn ${isQuestionFlagged(currentQuestionData.id) ? 'flagged' : ''}`}
                onClick={() => toggleQuestionFlag(currentQuestionData.id)}
                title={isQuestionFlagged(currentQuestionData.id) ? 'Unmark' : 'Mark for review'}
              >
                <FontAwesomeIcon icon={faFlag} />
                <span>Mark</span>
              </button>
            )}
          </div>

          {/* Center: empty – navigation moved to bottom bar */}
          <div className="sim-v2__toolbar-center" />

          {/* Right: Tool buttons + Timer */}
          <div className="sim-v2__toolbar-right">
            <button
              className={`sim-v2__tool-btn ${isFullScreen ? 'active' : ''}`}
              onClick={toggleFullScreen}
              title="Toggle Full Screen"
            >
              <FontAwesomeIcon icon={isFullScreen ? faCompress : faExpand} className="tool-icon" />
              <span className="tool-label">Full Screen</span>
            </button>
            <button
              className={`sim-v2__tool-btn ${showLabValues ? 'active' : ''}`}
              onClick={() => { setShowLabValues(!showLabValues); setShowSettings(false); }}
              title="Lab Values"
            >
              <FontAwesomeIcon icon={faFlask} className="tool-icon" />
              <span className="tool-label">Lab Values</span>
            </button>
            <button
              className={`sim-v2__tool-btn ${showCalculator ? 'active' : ''}`}
              onClick={() => setShowCalculator(!showCalculator)}
              title="Calculator"
            >
              <FontAwesomeIcon icon={faCalculator} className="tool-icon" />
              <span className="tool-label">Calculator</span>
            </button>
            <button
              className="sim-v2__settings-btn"
              onClick={() => { setShowSettings(!showSettings); }}
              title="Settings"
            >
              <FontAwesomeIcon icon={faCog} />
            </button>

            {/* Timer Display */}
            {showTimer && (
              <div className="sim-v2__timer-display">
                <div className="sim-v2__timer-item">
                  <span className="sim-v2__timer-label">Time Remaining</span>
                  <span className={`sim-v2__timer-value ${timeRemaining < 1800 ? 'warning' : ''}`}>
                    {formatTime(timeRemaining)}
                  </span>
                </div>
              </div>
            )}

            {(sessionState === 'paused' || sessionState === 'break_running') && (
              <span className="sim-v2__paused-badge">
                {sessionState === 'break_running' ? 'BREAK' : 'PAUSED'}
              </span>
            )}
          </div>
        </div>

        {/* ===== MAIN CONTENT AREA ===== */}
        <div className="sim-v2__main">
          {/* Left Question List Sidebar */}
          <div className={`sim-v2__qlist-sidebar ${!showNavPanel ? 'hidden' : ''}`}>
            <div className="sim-v2__qlist-items">
              {Array.from({ length: getQuestionsPerBlock() }, (_, i) => i + 1).map(qNum => {
                const qData = questions[qNum - 1];
                const isAnswered = qData && answers[qData.id];
                const isFlaggedQ = qData && isQuestionFlagged(qData.id);
                const isCurrent = qNum === currentQuestion;

                return (
                  <div
                    key={qNum}
                    className={`sim-v2__qlist-item ${isCurrent ? 'current' : ''} ${isAnswered ? 'answered' : ''} ${isFlaggedQ ? 'flagged' : ''}`}
                    onClick={() => navigateToQuestion(qNum)}
                    title={`Question ${qNum}${isAnswered ? ' (Answered)' : ''}${isFlaggedQ ? ' (Flagged)' : ''}`}
                  >
                    {qNum}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Question Panel */}
          <div className="sim-v2__question-panel" style={{ fontSize: `${getFontSizePx()}px` }}>
            <div className="sim-v2__question-scroll">
              {error && (
                <div className="sim-v2__error">
                  {error}
                  {isQuotaErrorState && (
                    <div style={{ marginTop: '8px' }}>
                      <Link to="/usage" style={{ color: 'var(--sim-accent)', fontWeight: 600 }}>
                        Click Here to Upgrade your plan
                      </Link>
                    </div>
                  )}
                </div>
              )}

              {currentQuestionData ? (
                <div className="sim-v2__question-body">
                  {/* Question Text */}
                  <div className="sim-v2__question-text">
                    <div dangerouslySetInnerHTML={{
                      __html: currentQuestionData.questionText ||
                        currentQuestionData.text ||
                        currentQuestionData.body ||
                        'Question text not available'
                    }} />
                  </div>

                  {/* Question Assets */}
                  <div className="sim-v2__assets">
                    <AssetDisplay
                      assets={currentQuestionAssets}
                      isLoading={isLoadingAssets}
                      error={assetsError}
                      isDarkMode={examTheme === 'dark'}
                    />
                  </div>

                  {/* Lab Data Table */}
                  <LabTable
                    currentQuestion={currentQuestionData}
                    isDarkMode={examTheme === 'dark'}
                  />

                  {/* Answer Options */}
                  <div className="sim-v2__answers">
                    <ExamAnswerOptions
                      ref={examAnswerOptionsRef}
                      key={`question-${currentQuestionData.id}`}
                      currentQuestion={currentQuestionData}
                      selectedAnswer={selectedAnswer}
                      setSelectedAnswer={setSelectedAnswer}
                      isAnswerSubmitted={isAnswerSubmitted}
                      isCorrect={false}
                      isFlagged={isQuestionFlagged(currentQuestionData.id)}
                      isDarkMode={examTheme === 'dark'}
                      examSession={examSession}
                      onAnswerSubmitted={handleAnswerSubmitted}
                      onAnswersLoaded={handleAnswersLoaded}
                      submitTrigger={submitTrigger}
                      hideButtons={true}
                      isLocked={false}
                    />
                  </div>
                </div>
              ) : (
                <div className="sim-v2__loading">
                  {loading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin />
                      <span>Loading question...</span>
                    </>
                  ) : (
                    questions.length > 0 ? 'Loading question...' : 'No questions available'
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Lab Values Sidebar */}
          <div className={`sim-v2__sidebar ${!showLabValues ? 'hidden' : ''}`}>
            {showLabValues && (
              <LabValueSidebar
                isDarkMode={examTheme === 'dark'}
                toggleSidebar={() => setShowLabValues(false)}
                isVisible={showLabValues}
              />
            )}
          </div>
        </div>

        {/* ===== BOTTOM TOOLBAR ===== */}
        <div className="sim-v2__bottom-bar">
          {/* Left: Block time elapsed */}
          <div className="sim-v2__bottom-left">
            <span className="sim-v2__block-time-label">
              Block Time: {formatTime(blockTimeRemaining)}
            </span>
          </div>

          {/* Center: Previous · Submit/Update Answer · Next */}
          <div className="sim-v2__bottom-center">
            <button
              className="sim-v2__nav-btn"
              onClick={() => navigateToQuestion(currentQuestion - 1)}
              disabled={currentQuestion === 1}
              title="Previous question"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
              <span>Previous</span>
            </button>

            <button
              className="sim-v2__bottom-btn sim-v2__submit-btn"
              onClick={() => {
                if (examAnswerOptionsRef.current && examAnswerOptionsRef.current.submitAnswer) {
                  examAnswerOptionsRef.current.submitAnswer();
                } else {
                  setSubmitTrigger(prev => prev + 1);
                }
              }}
              disabled={!selectedAnswer}
              title={isAnswerSubmitted ? 'Update your answer' : 'Submit your answer'}
              style={{
                background: !selectedAnswer
                  ? 'var(--sim-border)'
                  : isAnswerSubmitted
                    ? 'var(--sim-warning)'
                    : 'var(--sim-success)',
                color: !selectedAnswer ? 'var(--sim-text-secondary)' : '#fff',
                fontWeight: 700,
                padding: '6px 22px',
                borderRadius: '4px',
                fontSize: '13px',
                cursor: !selectedAnswer ? 'not-allowed' : 'pointer',
                opacity: !selectedAnswer ? 0.6 : 1,
                transition: 'background 0.2s, opacity 0.2s'
              }}
            >
              {isAnswerSubmitted ? 'Update Answer' : 'Submit Answer'}
            </button>

            <button
              className="sim-v2__nav-btn"
              onClick={() => navigateToQuestion(currentQuestion + 1)}
              disabled={currentQuestion === getQuestionsPerBlock()}
              title="Next question"
            >
              <span>Next</span>
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>

          {/* Right: Action buttons */}
          <div className="sim-v2__bottom-right">
            <button
              className={`sim-v2__bottom-btn ${showNavPanel ? 'active' : ''}`}
              onClick={() => setShowNavPanel(!showNavPanel)}
              title="Toggle Question Navigator"
            >
              <FontAwesomeIcon icon={faTh} />
              <span className="bottom-label">Navigator</span>
            </button>
            <button
              className="sim-v2__bottom-btn"
              onClick={endCurrentBlock}
              disabled={completedBlocks.has(currentBlock)}
              title="End Block"
            >
              <FontAwesomeIcon icon={faStop} />
              <span className="bottom-label">End Block</span>
            </button>
            <button
              className="sim-v2__bottom-btn"
              onClick={handlePauseResume}
              disabled={sessionState === 'break_running' || sessionState === 'finished'}
              title={sessionState === 'paused' ? 'Resume' : 'Pause'}
            >
              <FontAwesomeIcon icon={sessionState === 'paused' ? faPlay : faPause} />
              <span className="bottom-label">{sessionState === 'paused' ? 'Resume' : 'Pause'}</span>
            </button>
            <button
              className="sim-v2__bottom-btn"
              onClick={handleTakeBreak}
              disabled={sessionState === 'break_running' || sessionState === 'finished'}
              title="Take Break"
            >
              <FontAwesomeIcon icon={faCoffee} />
              <span className="bottom-label">Break</span>
            </button>
            <button
              className="sim-v2__bottom-btn danger"
              onClick={() => setShowEndSessionModal(true)}
              title="End Exam"
            >
              <FontAwesomeIcon icon={faStopCircle} />
              <span className="bottom-label">End Exam</span>
            </button>
          </div>
        </div>

        {/* ===== CALCULATOR ===== */}
        <Calculator isOpen={showCalculator} onClose={() => setShowCalculator(false)} />

        {/* ===== SETTINGS PANEL ===== */}
        {showSettings && renderSettings()}

        {/* ===== END BLOCK MODAL ===== */}
        {showEndBlockModal && (
          <div className="sim-v2__modal-overlay" onClick={cancelEndBlock}>
            <div className="sim-v2__modal" onClick={(e) => e.stopPropagation()}>
              <div className="sim-v2__modal-header">
                <h3>End Block {currentBlock}</h3>
              </div>
              <div className="sim-v2__modal-body">
                <p>Are you sure you want to end Block {currentBlock}?</p>
                <div className="sim-v2__modal-warning">
                  <span className="warn-icon">⚠️</span>
                  <span className="warn-text">
                    <strong>Warning:</strong> Once you end this block, you cannot return to it.
                    Any unanswered questions will remain unanswered.
                  </span>
                </div>
              </div>
              <div className="sim-v2__modal-footer">
                <button className="sim-v2__modal-cancel" onClick={cancelEndBlock}>Cancel</button>
                <button className="sim-v2__modal-confirm" onClick={confirmEndBlock}>End Block</button>
              </div>
            </div>
          </div>
        )}

        {/* ===== END SESSION MODAL ===== */}
        {showEndSessionModal && (
          <div className="sim-v2__modal-overlay" onClick={handleCancelEndSession}>
            <div className="sim-v2__modal" onClick={(e) => e.stopPropagation()}>
              <div className="sim-v2__modal-header">
                <h3>End Exam Session</h3>
              </div>
              <div className="sim-v2__modal-body">
                <p>Are you sure you want to end this exam session?</p>
                <div className="sim-v2__modal-warning">
                  <span className="warn-icon">⚠️</span>
                  <span className="warn-text">This action cannot be undone. Your progress will be saved.</span>
                </div>
              </div>
              <div className="sim-v2__modal-footer">
                <button className="sim-v2__modal-cancel" onClick={handleCancelEndSession}>Cancel</button>
                <button className="sim-v2__modal-confirm primary" onClick={handleConfirmEndSession}>End Session</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Fallback
  return (
    <div className={themeClass}>
      <div className="sim-v2__setup">
        <div className="sim-v2__setup-card">
          <h1 className="sim-v2__setup-title">Error</h1>
          <p>Unexpected view state: {currentView}</p>
          <button className="sim-v2__btn-primary" onClick={() => setCurrentView('setup')}>Return to Setup</button>
        </div>
      </div>
    </div>
  );
};

export default SimulateExamV2;
